import React, { type FunctionComponent } from 'react';
import { type Shell } from '#packages/apilib';
import * as util from '#packages/util';
import {
  EditorAPIKey,
  ThemeApiKey,
  WorkspaceRightPanelApiKey,
  RtlLtrSettingsManagerKey,
} from '#packages/apis';
import type { A11yWizardAdapterAPI } from './a11yWizardAdapterAPI';
import { WORKSPACE_RIGHT_PANEL_NAMES } from '#packages/constants';
import type {
  DynamicPageLink,
  PagesData,
  SerializedCompStructure,
  StyleRef,
  ColorPalette,
} from 'types/documentServices';
import type {
  ProgressBarOptions,
  ColorPickerOptions,
  CompRef as CompRefData,
  ComponentData,
  ComponentStyle,
  PagesDataType,
  DialogOptions,
  OpenPanelConfig,
} from '@wix/a11y-adapters/editor';
import {
  getDesktopBreakpointDefinition,
  getMobileBreakpointDefinition,
} from '#packages/breakpoints';
import {
  CLASSIC_ENTRY_POINT,
  EDITOR_APP_ID,
  convertCompRefTypeToString,
  convertCompRefTypeToViewMode,
  convertStyleTypeToWizard,
  getDisplayedComponents,
  isImageXData,
  showConfirmationPanel,
  showErrorPanel,
  transformStyle,
  viewModes,
} from './utils';
// eslint-disable-next-line @wix/santa-editor/scoped-imports
import {
  getAdvancedSettings,
  setAdvancedSettings,
} from '@/platform/api/editor/accessibility/accessibility';
import { getAppPagesSections } from '#packages/panels';

export function createA11yWizardAdapterAPI(shell: Shell): A11yWizardAdapterAPI {
  const workspaceRightPanelApi = shell.getAPI(WorkspaceRightPanelApiKey);
  const editorAPI = shell.getAPI(EditorAPIKey);
  const themeAPI = shell.getAPI(ThemeApiKey);
  const rtlAPI = shell.getAPI(RtlLtrSettingsManagerKey);

  const contributePanel = (shell: Shell, component: FunctionComponent<{}>) => {
    workspaceRightPanelApi.contributePanelContent(
      () => React.createElement(component),
      {
        panelName: WORKSPACE_RIGHT_PANEL_NAMES.A11Y_WIZARD,
        onOpen: () => {
          workspaceRightPanelApi.open('a11yWizard', {
            panelName: WORKSPACE_RIGHT_PANEL_NAMES.A11Y_WIZARD,
            panelWidth: 306,
            title: '',
          });
        },
      },
    );
  };

  const getAllStaticPages = () => {
    const pageIdsToFilter = new Set<string>();
    const staticPages: PagesData[] = editorAPI.pages.getPagesData();

    const dynamicPagesList =
      editorAPI.dsRead.routers.pages.getDynamicPagesList();

    dynamicPagesList
      .flatMap((dynamicPage) => dynamicPage.pages)
      .forEach((page) => {
        if (page.id) {
          pageIdsToFilter.add(page.id);
        }
      });

    const filteredPages = staticPages.filter(
      (page): page is PagesData & { id: string } =>
        !!page.id && !pageIdsToFilter.has(page.id),
    );

    return filteredPages as PagesDataType[];
  };

  const getAllDynamicPages = () => {
    const dynamicPageIds = new Set<string>();

    const allPages = editorAPI.pages.getPagesData();

    const dynamicPagesList =
      editorAPI.dsRead.routers.pages.getDynamicPagesList();

    dynamicPagesList
      .map((dynamicPage) => dynamicPage.pages)
      .flat()
      .forEach(({ id }) => dynamicPageIds.add(id));

    const dynamicPages = allPages.filter(
      (page): page is PagesData & { id: string } =>
        !!page.id && dynamicPageIds.has(page.id),
    );

    return dynamicPages as PagesDataType[];
  };

  const getHomePageId = () => {
    return editorAPI.homePage.get();
  };

  const getAllPagesData = () => {
    const pagesData: PagesDataType[] =
      editorAPI.pages.getPagesData() as PagesDataType[];
    const membersCustomPages = getMembersCustomPages();
    if (membersCustomPages.signIn.id) {
      pagesData.push({
        ...editorAPI.pages.data.get(membersCustomPages.signIn.id),
        managingAppDefId: 'main',
      } as PagesDataType);
    }
    if (membersCustomPages.signUp.id) {
      pagesData.push({
        ...editorAPI.pages.data.get(membersCustomPages.signUp.id),
        managingAppDefId: 'main',
      } as PagesDataType);
    }
    const pagesByGroup = getAppPagesSections(editorAPI, pagesData);
    const nonGeneralPageIdPagesGroupMap = Object.fromEntries(
      pagesByGroup.flatMap((group) =>
        group.pages.map((page) => [
          page.id,
          {
            name: group.title,
            groupId: group.id,
            appDefinitionId: group.id,
          },
        ]),
      ),
    );
    return { pagesData, nonGeneralPageIdPagesGroupMap };
  };

  const getComponent = (compRefData: CompRefData) => {
    const compRef = convertCompRefTypeToViewMode(compRefData);
    const componentData = editorAPI.components.data.get(compRef);

    const componentLabel =
      util.componentLabel.getComponentLabel(editorAPI, compRef) ||
      editorAPI.components.getDisplayName(compRef);

    const compStyle = editorAPI.components.style.get(compRef);

    let componentStyle = compStyle;
    if (compStyle?.style) {
      componentStyle = {
        ...compStyle,
        style: transformStyle(compStyle?.style),
      };
    }

    return {
      id: compRef.id,
      compRef: convertCompRefTypeToString(compRef),
      parentId: editorAPI.components.getContainer(compRef)?.id,
      isSharedBlock: false,
      sharedBlockMasterParentId: '',
      type: editorAPI.components.getType(compRef),
      data: componentData,
      design: editorAPI.components.design.get(compRef),
      style: componentStyle,
      isConnectedToDataBinding: false,
      isHidden: componentData?.metaData?.isHidden,
      stylesInAllBreakpoints: {},
      hiddenInAllBreakpoints: {},
      label: componentLabel,
    };
  };

  const getComponentAncestors = (compRefData: CompRefData) => {
    const compRef = convertCompRefTypeToViewMode(compRefData);
    return editorAPI.components.getAncestors(compRef);
  };

  const getComponentsByType = (pageId: string, type: string) => {
    const displayedComponents = editorAPI.components.getAllComponents(pageId);
    return displayedComponents.filter(
      (component) => editorAPI.components.getType(component) === type,
    );
  };

  const getAllComponents = (pageId: string) => {
    const displayedComponents = getDisplayedComponents(editorAPI, pageId);
    return displayedComponents;
  };

  const getColorsThemesMap = () => {
    return themeAPI.colors.getAll() as ColorPalette;
  };

  const getStylesThemeMap = () => {
    const styles = editorAPI.theme.styles.getAll();
    // TODO fix this
    // @ts-expect-error
    const styleThemeMap = convertStyleTypeToWizard(styles);
    return styleThemeMap;
  };

  const getFontsThemesMap = () => {
    return editorAPI.theme.textThemes.getAll();
  };

  const getIsMultilingualEnabled = () => {
    return editorAPI.language.multilingual.isEnabled();
  };

  const getCurrentLanguage = () => {
    return editorAPI.language.current.getDefinition();
  };

  const getMultilingualLanguages = () => {
    return editorAPI.language.getFull();
  };

  const getIsSiteAutoDOMOrderEnabled = () => {
    return editorAPI.dsRead.accessibility.autoDomReorder.get();
  };

  const getCurrentPageId = () => {
    return editorAPI.dsRead.pages.getCurrentPageId();
  };

  const getPageBreakpoints = () => {
    const desktopBreakpoint = {
      ...getDesktopBreakpointDefinition(),
    };
    const mobileBreakpoint = {
      ...getMobileBreakpointDefinition(),
      id: 'mobile',
    };
    const breakpoints = [desktopBreakpoint, mobileBreakpoint];
    return breakpoints;
  };

  const getGlobalSectionsBreakpoints = () => {
    return {};
  };

  const getPremiumPackagePickerUrl = () => {
    const metaSiteId = editorAPI.dsRead.generalInfo.getMetaSiteId();
    const referralInfo = 'referralAdditionalInfo=a11y-wizard';

    let url =
      util.serviceTopology.premiumServerUrl || 'https://premium.wix.com';
    url += `/wix/api/premiumStart?${referralInfo}&siteGuid=${metaSiteId}`;
    return url;
  };

  const getEntryPoint = () => {
    return CLASSIC_ENTRY_POINT;
  };

  const getHasSkipToMainContent = () => {
    return editorAPI.dsRead.accessibility.skipToContent.get();
  };

  const getHasVisualFocus = () => {
    return editorAPI.dsRead.accessibility.visualFocus.get();
  };

  const getHasAdvancedSettings = async () => {
    const settings = await getAdvancedSettings(
      editorAPI,
      { appDefinitionId: EDITOR_APP_ID },
      'token',
    );
    return !!settings;
  };

  const getIsComponentInGlobalSection = (compRefData: CompRefData) => {
    const compRef = convertCompRefTypeToViewMode(compRefData);
    const isShownOnAllPages = editorAPI.components.isShowOnAllPages(compRef);
    const isShownOnSomePages = editorAPI.components.isShowOnSomePages(compRef);

    return isShownOnAllPages || isShownOnSomePages;
  };

  const getHasAutoSave = () => {
    return editorAPI.autosaveManager.isAutosaveEnabled();
  };

  const getIsSiteLanguageRTL = () => {
    const currentLang = editorAPI.generalInfo.getRegionalLanguage();
    const langDirection = rtlAPI.getSiteLangDirection(currentLang);
    return langDirection === 'rtl';
  };

  const getIsSiteDirectionMatchingLanguageDirection = () => {
    return rtlAPI.isSiteDirectionAlignWithLangDirection();
  };

  const getHasSavePermission = () => {
    return editorAPI.dsRead.generalInfo
      .getUserPermissions()
      .includes('html-editor.save');
  };

  const getCurrentBranchId = () => '00000000-0000-0000-0000-000000000000';

  const getPublishedBranchId = () => editorAPI.branches.getPublishedBranchId();

  const getMembersCustomPages = () => {
    const signInId = editorAPI.siteMembers.getCustomSignInPageId() || '';
    const signUpId = editorAPI.siteMembers.getCustomSignupPageId() || '';
    return {
      signIn: {
        id: signInId,
        title: editorAPI.pages.getPageTitle(signInId),
      },
      signUp: {
        id: signUpId,
        title: editorAPI.pages.getPageTitle(signUpId),
      },
    };
  };

  /** ******* **/
  /** Setters **/
  /** ******* **/

  const setPageTitle = async (pageId: string, title: string) => {
    editorAPI.menus.renamePage(pageId, '', title);
  };

  const setComponentStyle = (
    compRefData: CompRefData,
    style: ComponentStyle,
  ) => {
    const compRef = convertCompRefTypeToViewMode(compRefData);
    editorAPI.components.style.update(compRef, style as StyleRef);
  };

  const setComponentData = (compRefData: CompRefData, data: ComponentData) => {
    const compRef = convertCompRefTypeToViewMode(compRefData);
    editorAPI.components.data.update(compRef, data);
  };

  const setComponentDesign = (
    compRefData: CompRefData,
    design: SerializedCompStructure['design'],
  ) => {
    const compRef = convertCompRefTypeToViewMode(compRefData);
    editorAPI.components.design.update(compRef, design);
  };

  const setImageAltText = (compRefData: CompRefData, altText: string) => {
    const compRef = convertCompRefTypeToViewMode(compRefData);
    const compData = editorAPI.components.data.get(compRef);
    if (isImageXData(compData)) {
      editorAPI.components.data.update(
        compRef,
        { image: { alt: altText } },
        true,
      );
      return;
    }
    editorAPI.components.data.update(compRef, { alt: altText }, true);
  };

  const setIsDecorativeImage = (
    compRefData: CompRefData,
    isDecorative: boolean,
  ) => {
    const compRef = convertCompRefTypeToViewMode(compRefData);
    const compData = editorAPI.components.data.get(compRef);
    if (isImageXData(compData)) {
      editorAPI.components.data.update(
        compRef,
        { image: { isDecorative } },
        true,
      );
      return;
    }
    editorAPI.components.data.update(
      compRef,
      { decorative: isDecorative },
      true,
    );
  };

  const setSkipToMainContent = (value: boolean) => {
    editorAPI.dsActions.accessibility.skipToContent.set(value);
  };

  const setVisualFocus = (value: boolean) => {
    editorAPI.dsActions.accessibility.visualFocus.set(value);
  };

  const setUserAdvancedSettings = (value: boolean) => {
    setAdvancedSettings(
      editorAPI,
      { appDefinitionId: EDITOR_APP_ID },
      'token',
      { value },
    );
  };

  /** ******* **/
  /** Actions **/
  /** ******* **/

  const enablePanel = async (openPanelConfig: OpenPanelConfig) => {
    workspaceRightPanelApi.open('a11yWizard', {
      panelName: WORKSPACE_RIGHT_PANEL_NAMES.A11Y_WIZARD,
      panelWidth: 306,
      title: openPanelConfig.title,
      renderHeader: false,
      enterZoomMode: false,
      leavePanelOpen: true,
    });
  };

  const disablePanel = async () => {
    editorAPI.panelManager.closeAllPanels();
    workspaceRightPanelApi.close('a11yWizard');
  };

  const togglePanel = () => {
    editorAPI.panelManager.closeAllPanels();
    workspaceRightPanelApi.close('a11yWizard');
  };

  const moveToPage = async (pageId: string, innerRoute?: string) => {
    const pageIdOrPageLink: DynamicPageLink | string = innerRoute
      ? {
          pageId,
          innerRoute,
          routerId: pageId,
          isTpaRoute: true,
          type: 'DynamicPageLink',
        }
      : pageId;
    await editorAPI.pages.navigateTo(pageIdOrPageLink);
  };

  const selectComponent = async (compRefData: CompRefData) => {
    const compRef = convertCompRefTypeToViewMode(compRefData);
    if (compRef.type === viewModes.MOBILE && editorAPI.isDesktopEditor()) {
      editorAPI.setMobileEditor();
      await editorAPI.dsActions.waitForChangesAppliedAsync();
    }
    if (compRef.type === viewModes.DESKTOP && editorAPI.isMobileEditor()) {
      editorAPI.setDesktopEditor('a11y_panel_selectComponent', true);
      await editorAPI.dsActions.waitForChangesAppliedAsync();
    }
    editorAPI.components.componentLocator.locateComponentAndHighlight(compRef);
  };

  const enableSiteAutoDOMOrder = async () => {
    editorAPI.dsActions.accessibility.autoDomReorder.set(true);
    await editorAPI.dsActions.waitForChangesAppliedAsync();
  };

  const disableSiteAutoDOMOrder = async () => {
    editorAPI.dsActions.accessibility.autoDomReorder.set(false);
    await editorAPI.dsActions.waitForChangesAppliedAsync();
  };

  const reorganizePageDOMOrder = async () => {
    return;
  };

  const reorganizeAllPagesDOMOrder = async () => {
    await rtlAPI.toggleSiteDirection({
      biParams: {
        origin: 'accessibility-wizard',
      },
      shouldShowNotification: true,
    });
  };

  const showDialog = async (dialogOptions: DialogOptions) => {
    if (dialogOptions.theme === 'destructive') {
      await showErrorPanel(editorAPI, dialogOptions);
      return;
    }
    await showConfirmationPanel(editorAPI, dialogOptions);
  };

  const showProgressBar = (options: ProgressBarOptions) => {
    editorAPI.panelHelpers.openProgressBar({
      title: options.title,
      totalSteps: options.totalSteps,
      currentStep: options.getStep(),
    });
    let current = options.getStep() + 1;
    const interval = setInterval(() => {
      if (current < options.totalSteps) {
        editorAPI.panelHelpers.updateProgressBar(
          current,
          `${options.descriptionLines[current]}`,
        );
        current++;
      } else {
        clearInterval(interval);
        current = 0;
      }
    }, 400);
    return async () => {
      editorAPI.panelHelpers.closeProgressBar();
    };
  };

  const openArticle = (articleKey: string) => {
    editorAPI.panelManager.openHelpCenter(articleKey);
  };

  const openColorPicker = (options: ColorPickerOptions) => {
    return editorAPI.openColorPicker(
      options.value,
      {
        top: options.top,
        left: options.left,
      },
      {
        onChange: (color: any) => options.onChange(color),
        onClose: options.onClose(),
        enableHistory: false,
        allowPaletteEditing: true,
        previewOnHover: true,
      },
    );
  };

  const openDashboard = async (tpaAppId: string) => {
    const appData = editorAPI.dsRead.platform.getAppDataByAppDefId(tpaAppId);
    editorAPI.platform.panels.openDashboardPanel(appData, {
      url: 'editorx',
    });
  };

  const getPanelState = () => {
    throw new Error('Not implemented yet');
  };

  const triggerEditorSave = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      editorAPI.saveManager.saveInBackground(resolve, reject, 'a11y-wizard');
    });
  };

  const flipSiteDomDirectionFlag = async () => {
    await rtlAPI.toggleSiteDirection({
      biParams: {
        origin: 'accessibility-wizard',
      },
      shouldShowNotification: false,
    });
    await editorAPI.waitForChangesAppliedAsync();
  };

  const reloadSiteProperties = () => {
    editorAPI.documentServices.generalInfo.sitePropertiesInfo.reload();
  };

  //as for now santa editor doesnt support builder components
  const isBuilderComponent = (_compRefData: CompRefData) => false;

  const setBackgroundForBuilderComponent = (
    _compRef: CompRefData,
    _elementClass: string,
    _color: string,
  ) => {
    return;
  };

  const setTextColorForBuilderComponent = (
    _compRef: CompRefData,
    _elementClass: string,
    _color: string,
  ) => {
    return;
  };

  //as for now santa editor doesnt dom order for components
  const enableComponentAutoDOMOrder = () => {
    return;
  };

  //this function is not used in the santa - only in editor 3
  const getPagesData = () => {
    return {
      mainPages: [] as PagesDataType[],
      pageToGroupMap: {},
    };
  };

  return {
    /** ******* **/
    /** Getters **/
    /** ******* **/
    contributePanel,
    getAllStaticPages,
    getMembersCustomPages,
    getAllPagesData,
    getAllDynamicPages,
    getComponentAncestors,
    getAllComponents,
    getComponent,
    getComponentsByType,
    getHomePageId,
    getColorsThemesMap,
    getStylesThemeMap,
    getFontsThemesMap,
    getIsMultilingualEnabled,
    getCurrentLanguage,
    getMultilingualLanguages,
    getIsSiteAutoDOMOrderEnabled,
    getCurrentPageId,
    getPageBreakpoints,
    getGlobalSectionsBreakpoints,
    getPremiumPackagePickerUrl,
    getEntryPoint,
    getHasSkipToMainContent,
    getHasVisualFocus,
    getHasAdvancedSettings,
    getHasAutoSave,
    getIsSiteLanguageRTL,
    getIsSiteDirectionMatchingLanguageDirection,
    getIsComponentInGlobalSection,
    getHasSavePermission,
    getCurrentBranchId,
    getPublishedBranchId,
    isBuilderComponent,
    getPagesData,

    /** ******* **/
    /** Setters **/
    /** ******* **/
    setPageTitle,
    setComponentStyle,
    setComponentData,
    setComponentDesign,
    setImageAltText,
    setSkipToMainContent,
    setVisualFocus,
    setUserAdvancedSettings,
    setBackgroundForBuilderComponent,
    setTextColorForBuilderComponent,
    setIsDecorativeImage,

    /** ******* **/
    /** Actions **/
    /** ******* **/
    showProgressBar,
    openArticle,
    enablePanel,
    disablePanel,
    togglePanel,
    moveToPage,
    selectComponent,
    enableSiteAutoDOMOrder,
    disableSiteAutoDOMOrder,
    reorganizePageDOMOrder,
    reorganizeAllPagesDOMOrder,
    showDialog,
    openColorPicker,
    openDashboard,
    getPanelState,
    triggerEditorSave,
    flipSiteDomDirectionFlag,
    reloadSiteProperties,
    enableComponentAutoDOMOrder,
  };
}
